import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Customer, customers as customersApi, Geofence, geofences } from '../api';
import { DataGrid } from '../components/DataGrid';
import { FleetMap } from '../components/FleetMap';
import { Link } from '../components/Links';
import { SearchSelect } from '../components/SearchSelect';
import { dayOnly } from '../format';

// Size in words: hectares under 1 km², else km².
export const areaText = (m2: number) => (m2 < 1_000_000
  ? `${(m2 / 10_000).toLocaleString('en-IE', { maximumFractionDigits: m2 < 100_000 ? 2 : 1 })} ha`
  : `${(m2 / 1_000_000).toLocaleString('en-IE', { maximumFractionDigits: 1 })} km²`);
export const shapeText = (g: Pick<Geofence, 'kind' | 'circle' | 'points'>) => (g.kind === 'circle' && g.circle
  ? `Circle, ${g.circle.radiusM.toLocaleString('en-IE')} m` : `Polygon, ${g.points.length} corners`);
export const ownerText = (g: Pick<Geofence, 'customer'>) => g.customer?.name ?? 'Company-wide (staff only)';

export function Swatch({ colour }: { colour: string }) {
  return <span className="geo-dot" aria-hidden="true" style={{ '--geo': `var(--geofence-${colour})` } as CSSProperties} />;
}

// All geofences: a list beside a map of the ones shown, searchable, filtered by owner.
export function Geofences() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const owner = params.get('owner') ?? '';
  const [typed, setTyped] = useState(q);
  const [rows, setRows] = useState<Geofence[] | null>(null);
  const [custs, setCusts] = useState<Customer[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      if (typed.trim() !== q) setParams((p) => { typed.trim() ? p.set('q', typed.trim()) : p.delete('q'); return p; }, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [typed, q, setParams]);

  useEffect(() => { customersApi.list().then((r) => setCusts(r.customers)).catch(() => {}); }, []);
  useEffect(() => {
    geofences.list(q, owner === 'none' ? 'none' : owner ? Number(owner) : null).then((r) => setRows(r.geofences)).catch((e) => setError(e.message));
  }, [q, owner]);

  const ownerOptions = useMemo(() => [
    { value: 'none', label: 'Company-wide (staff only)' },
    ...custs.map((c) => ({ value: String(c.id), label: c.name })),
  ], [custs]);

  return (
    <div className="page wide">
      <div className="page-head">
        <h1>Geofences</h1>
        <button className="primary" onClick={() => navigate('/geofences/new')}>Add geofence</button>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="toolbar">
        <label className="sr-only" htmlFor="geo-search">Search</label>
        <input id="geo-search" type="search" placeholder="Search name or customer…" value={typed} onChange={(e) => setTyped(e.target.value)} />
        <label className="sr-only" htmlFor="geo-owner">Owner</label>
        <SearchSelect id="geo-owner" value={owner} options={ownerOptions} emptyLabel="All owners"
          onChange={(v) => setParams((p) => { v ? p.set('owner', v) : p.delete('owner'); return p; }, { replace: true })} />
      </div>
      <div className="geo-list-map card">
        <FleetMap geofences={rows ?? []} fitKey={`${q}|${owner}|${rows?.length ?? -1}`} ready={rows !== null} />
      </div>
      {rows && (
        <DataGrid id="geofences" rows={rows} rowKey={(g) => g.id} noun={['geofence', 'geofences']} href={(g) => `/geofences/${g.id}`}
          defaultSort={{ key: 'name', dir: 1 }} empty={q || owner ? 'No geofences match.' : 'No geofences yet. Add one to see when vehicles arrive and leave.'} columns={[
            { key: 'name', label: 'Name', always: true, value: (g) => g.name,
              cell: (g) => <Link to={`/geofences/${g.id}`}><Swatch colour={g.colour} /><strong>{g.name}</strong></Link> },
            { key: 'owner', label: 'Owner', value: ownerText },
            { key: 'shape', label: 'Shape', value: shapeText, nosort: true },
            { key: 'size', label: 'Size', align: 'right', value: (g) => g.areaM2, csv: (g) => g.areaM2, cell: (g) => areaText(g.areaM2), nowrap: true },
            { key: 'inside', label: 'Vehicles inside', align: 'right', value: (g) => g.insideNow },
            { key: 'updated', label: 'Changed', value: (g) => g.updatedAt, sortBy: (g) => Date.parse(g.updatedAt),
              csv: (g) => dayOnly(g.updatedAt), cell: (g) => <>{dayOnly(g.updatedAt)}{g.updatedBy && <span className="muted"> · {g.updatedBy}</span>}</>, nowrap: true },
          ]} />
      )}
    </div>
  );
}
